import { CellContext } from '@tanstack/react-table';
import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { formatPrice, splitPrice } from '@/utils/formatters.ts';
import { CellSecondary } from '@/components/table/_fieldDefinitions/_base/CellSecondary.tsx';
import { EmptyValue, EMPTY_VALUE } from '@/components/ui/EmptyValue.tsx';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import { FieldDefinitionDto } from '@/components/table/types/TableDefinitionDto.ts';
import { moneyValueExportRender } from '@/components/table/_fieldDefinitions/exportRenders.ts';
import {
  getAmountDirection,
  transactionDirectionAriaLabels,
  transactionDirectionIcons,
} from '@/features/dashboard/financialTransactions/types/TransactionDirection.utils.ts';

/**
 * Kwota transakcji ze strzałką kierunku. Sam minus gubi się w kolumnie cyfr,
 * a osobnej kolumny kierunku backend nie daje.
 */
export const signedMoneyValueFieldRender = <T,>(
  info: CellContext<T, unknown>,
) => {
  const value = info.getValue() as MoneyValueDto | null;
  if (!value) return <EmptyValue />;

  const direction = getAmountDirection(value.amount);
  const Icon = transactionDirectionIcons[direction];
  const { main, secondary } = splitPrice(value.amount, value.currency);

  return (
    <span className="inline-flex items-center gap-1">
      <Icon
        aria-label={transactionDirectionAriaLabels[direction]}
        className="size-3.5 shrink-0 text-neutral-400"
      />
      <span>
        {main}
        <CellSecondary>{secondary}</CellSecondary>
      </span>
    </span>
  );
};

/**
 * Zero reszty nie jest kwotą, tylko brakiem tematu, więc idzie jak pusta wartość.
 * Backendowa reszta jest podpisana, ale znak niesie już strzałka w kolumnie kwoty —
 * dwa zapisy tej samej liczby na jednym ekranie czytały się jak dwie różne.
 */
export const unallocatedMoneyValueFieldRender = <T,>(
  info: CellContext<T, unknown>,
) => {
  const value = info.getValue() as MoneyValueDto | null;
  if (!value || value.amount === 0) return <EmptyValue />;

  const { main, secondary } = splitPrice(
    Math.abs(value.amount),
    value.currency,
  );
  return (
    <>
      {main}
      <CellSecondary>{secondary}</CellSecondary>
    </>
  );
};

/** Ta sama reszta w arkuszu: pauza na zerze, kwota bez znaku poza nim. */
export const unallocatedMoneyValueExportRender = (value: unknown): string => {
  const money = value as MoneyValueDto | null;
  if (!money || money.amount === 0) return EMPTY_VALUE;

  return formatPrice(Math.abs(money.amount), money.currency);
};

interface TransactionMoneyRenders {
  fieldCellRender: <T>(info: CellContext<T, unknown>) => unknown;
  exportRender: (value: unknown) => unknown;
}

/**
 * Kolumny pieniężne o zapisie własnym, nie typu pola. Komórka i eksport muszą
 * je czytać z jednego miejsca — inaczej arkusz drukuje 0,00 tam, gdzie tabela
 * stawia pauzę.
 */
const TRANSACTION_MONEY_FIELD_RENDERS: Record<string, TransactionMoneyRenders> =
  {
    totalValue: {
      fieldCellRender: signedMoneyValueFieldRender,
      exportRender: moneyValueExportRender,
    },
    unallocatedValue: {
      fieldCellRender: unallocatedMoneyValueFieldRender,
      exportRender: unallocatedMoneyValueExportRender,
    },
  };

export const getTransactionMoneyRenders = (
  field: FieldDefinitionDto,
): TransactionMoneyRenders | undefined =>
  field.type === FieldType.MONEY_VALUE
    ? TRANSACTION_MONEY_FIELD_RENDERS[field.fieldId]
    : undefined;
